import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithCustomToken,
} from 'firebase/auth';
import { getDatabase, ref as dbRef, onValue, off as dbOff } from 'firebase/database';
import { app } from '../firebase/firebaseApp';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [profile, setProfile] = useState(null);
  const [apiToken, setApiToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth(app);
    const db = getDatabase(app);

    let unsubscribeRoles = null;
    let unsubscribeIdToken = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (unsubscribeRoles) {
        unsubscribeRoles();
        unsubscribeRoles = null;
      }
      if (unsubscribeIdToken) {
        unsubscribeIdToken();
        unsubscribeIdToken = null;
      }

      if (firebaseUser) {
        setUser(firebaseUser);

        const userRef = dbRef(db, `users/${firebaseUser.uid}`);

        const handleUserSnapshot = (snap) => {
          const val = snap.val();
          if (val) {
            setProfile(val);
            if (val.roles) {
              const rolesObj = val.roles;
              const roleList = Object.keys(rolesObj).filter((key) => !!rolesObj[key]);
              setRoles(roleList);
            } else {
              setRoles([]);
            }
          } else {
            setProfile(null);
            setRoles([]);
          }
        };

        onValue(userRef, handleUserSnapshot);
        unsubscribeRoles = () => {
          dbOff(userRef, 'value', handleUserSnapshot);
        };

        const token = await firebaseUser.getIdToken();
        setApiToken(token);

        unsubscribeIdToken = onIdTokenChanged(auth, async (u) => {
          if (!u) {
            setApiToken(null);
            return;
          }
          const newToken = await u.getIdToken();
          setApiToken(newToken);
        });

        setLoading(false);
      } else {
        setUser(null);
        setProfile(null);
        setRoles([]);
        setApiToken(null);
        setLoading(false);
      }
    });

    return () => {
      if (unsubscribeRoles) unsubscribeRoles();
      if (unsubscribeIdToken) unsubscribeIdToken();
      unsubscribeAuth();
    };
  }, []);

  const signInWithGoogle = async () => {
    const auth = getAuth(app);
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  const signOutUser = async () => {
    const auth = getAuth(app);
    await signOut(auth);
  };

  const impersonateLogin = async (customToken) => {
    const auth = getAuth(app);
    await signInWithCustomToken(auth, customToken);
    return { success: true };
  };

  const hasRole = (role) => roles.includes(role);

  const value = {
    user,
    roles,
    profile,
    apiToken,
    loading,
    signInWithGoogle,
    signOutUser,
    hasRole,
    impersonateLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
